// Détection automatique de l'URL API basée sur l'environnement
export const getApiBase = () => {
  // En développement local
  if (typeof window !== 'undefined' && window.location.origin.includes('localhost')) {
    return 'http://localhost:8081';
  }

  // En production - Construire l'URL API à partir de l'URL courante
  if (typeof window !== 'undefined') {
    const currentHost = window.location.host;
    // Si on est sur le port 8080 (frontend), l'API est sur 8081
    const apiHost = currentHost.replace(':8080', ':8081');
    return `${window.location.protocol}//${apiHost}`;
  }

  // Fallback pour les cas où window n'est pas disponible
  const env = import.meta as ImportMeta & { env?: { VITE_API_BASE?: string } };
  return env.env?.VITE_API_BASE || '';
};

const RESERVED_CLIENT_ROUTE_SEGMENTS = new Set([
  "admin",
  "pm",
  "apporteur",
  "prompt-lab",
  "fdr",
  "feuille-de-route-ia",
  "scope",
  "scope-v2",
  "synthese",
  "missions",
  "cabinet",
  "finalisation",
  "pilotage",
  "offre-speciale",
  "veille-modeles",
]);

type StoredClientAuth = {
  email?: string | null;
  clientId?: string | null;
  token?: string | null;
};

type StoredPMClientSession = {
  token?: string | null;
  client?: {
    id?: string | null;
    email?: string | null;
  } | null;
};

type BrowserAuthHeaderOptions = {
  pathname?: string;
  fallbackClientEmail?: string | null;
  fallbackClientId?: string | null;
};

const safeParseStorageJson = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getStoredClientAuth = (clientId: string | null | undefined): StoredClientAuth | null => {
  if (!clientId || typeof window === "undefined") {
    return null;
  }

  return safeParseStorageJson<StoredClientAuth>(
    localStorage.getItem(`client_auth_${clientId}`)
  );
};

const getStoredPMClientSession = (): StoredPMClientSession | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return safeParseStorageJson<StoredPMClientSession>(
    localStorage.getItem("pm_client_session")
  );
};

export const getCurrentClientRouteClientId = (
  pathname?: string,
): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const currentPathname = pathname ?? window.location.pathname;
  const match = currentPathname.match(/^\/([^/]+)/);
  const firstSegment = match?.[1]?.trim();

  if (!firstSegment || RESERVED_CLIENT_ROUTE_SEGMENTS.has(firstSegment)) {
    return null;
  }

  return firstSegment;
};

export const getCurrentBrowserClientContext = (
  options: BrowserAuthHeaderOptions = {},
): { clientId: string | null; email: string | null } => {
  const pathname =
    options.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");

  let clientId = options.fallbackClientId?.trim() || null;
  let email = options.fallbackClientEmail?.trim() || null;

  if (pathname?.startsWith("/pm/")) {
    const session = getStoredPMClientSession();
    clientId = clientId ?? session?.client?.id?.trim() ?? null;
    email = email ?? session?.client?.email?.trim() ?? null;
    return { clientId, email };
  }

  const routeClientId = getCurrentClientRouteClientId(pathname);
  const clientSession = getStoredClientAuth(routeClientId);

  clientId = clientId ?? routeClientId ?? clientSession?.clientId?.trim() ?? null;
  email = email ?? clientSession?.email?.trim() ?? null;

  return { clientId, email };
};

/**
 * E-mail du salarié connecté, tel que le serveur l'a confirmé (`GET /v1/auth/session`,
 * `POST /v1/admin/validate-magic-link`) et mis en cache par `useAdminToken`.
 *
 * Usage : affichage et masquage UI seulement. Ce n'est PAS une preuve d'identité : depuis E0
 * l'identité est portée par le cookie de session `ailix_session` (HttpOnly), et l'en-tête
 * `x-user-email` n'est plus jamais posé par le navigateur — le backend l'ignore.
 */
export const getStoredEmployeeEmail = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const adminEmail = localStorage.getItem("admin_email")?.trim();
    return adminEmail && adminEmail.toLowerCase().endsWith("@ailix.ai") ? adminEmail : null;
  } catch {
    return null;
  }
};

export const getCurrentBrowserAuthHeaders = (
  options: BrowserAuthHeaderOptions = {},
): Record<string, string> => {
  const headers: Record<string, string> = {};

  if (typeof window === "undefined") {
    return headers;
  }

  // E0 : aucun `x-user-email`. L'identité salarié voyage dans le cookie de session
  // (credentials: "include" dans performFetch) ; le backend retire tout en-tête reçu.

  try {
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      headers["x-admin-token"] = adminToken;
    }
  } catch {
    // Ignore storage access errors
  }

  const clientContext = getCurrentBrowserClientContext(options);

  if (clientContext.clientId) {
    headers["x-client-id"] = clientContext.clientId;
  }

  if (
    clientContext.email &&
    clientContext.email.toLowerCase() !== getStoredEmployeeEmail()?.toLowerCase()
  ) {
    headers["x-client-user-email"] = clientContext.email;
  }

  return headers;
};

type ApiError = Error & {
  contentType?: string;
  responseText?: string;
  retryAfter?: number | null;
  status?: number;
};

// Configuration du système de retry
const MAX_RETRIES = 3; // Retry jusqu'à 3 fois (évite de surcharger le serveur lors d'un incident)
const INITIAL_RETRY_DELAY = 2000; // 2 secondes
const MAX_RETRY_DELAY = 8000; // 8 secondes max

// Flag pour tracker si le serveur est en mode down (évite les toasts multiples)
let isServerCurrentlyDown = false;

const createApiError = (
  message: string,
  extras: Partial<ApiError> = {},
): ApiError => Object.assign(new Error(message), extras);

/**
 * Détecte si une erreur est liée à un problème de connexion serveur
 */
const isNetworkError = (error: unknown): boolean => {
  const apiError = error as Partial<ApiError> | undefined;

  if (typeof apiError?.status === 'number' && [502, 503, 504].includes(apiError.status)) {
    return true;
  }

  // Détecter les erreurs de connexion réseau (fetch failed)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Détecter les erreurs HTTP liées au rebuild du serveur
  if (error instanceof Error && error.message) {
    const msg = error.message.toLowerCase();
    return msg.includes('http 502') ||
           msg.includes('http 503') ||
           msg.includes('http 504') ||
           msg.includes('network') ||
           msg.includes('failed to fetch');
  }

  return false;
};

/**
 * Endpoints qui interrogent une infra externe (Coolify) et qui pollent en continu.
 * Leurs 502/503/504 traduisent une lenteur/indispo de Coolify, PAS un rebuild du
 * backend Ailix. Ils sont donc exclus du mécanisme de résilience « rebuild » :
 * - pas de bandeau global « Nous mettons à jour l'outil » (pensé pour les clients)
 * - pas de retry agressif d'apiFetch (qui empilerait les requêtes sur un poll à 5s)
 * React Query garde son propre retry + refetchInterval comme filet local.
 */
const isBackgroundPollingEndpoint = (path: string): boolean =>
  path.includes('/v1/monitoring/');

/**
 * Détecte si la requête est une mutation (POST, PUT, DELETE, PATCH)
 * Les mutations ne doivent PAS être retried automatiquement pour éviter les duplications
 */
const isMutation = (init?: RequestInit): boolean => {
  const method = init?.method?.toUpperCase();
  return method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH';
};

/**
 * Utilitaire pour attendre un délai
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fonction interne pour effectuer la requête fetch avec les headers personnalisés
 */
const performFetch = async (path: string, init?: RequestInit) => {
  const base = getApiBase();
  const url = base ? `${base}${path}` : path;

  // Ajouter automatiquement le Content-Type pour les requêtes avec body
  const requestInit = { ...init };
  // E0 : le cookie de session salarié (`ailix_session`, HttpOnly, SameSite=Lax) doit voyager
  // avec chaque appel. En production, Netlify relaie `/v1/*` sous la même origine ; en dev,
  // le front (localhost:8080) et l'API (localhost:8081) sont deux origines du même site :
  // `include` couvre les deux (CORS backend : allow_credentials + origines explicites).
  requestInit.credentials = requestInit.credentials ?? "include";
  requestInit.headers = {
    ...getCurrentBrowserAuthHeaders(),
    ...(requestInit.headers || {}),
  };

  if (
    requestInit.body &&
    !requestInit.headers?.["content-type"] &&
    !requestInit.headers?.["Content-Type"]
  ) {
    requestInit.headers = {
      "Content-Type": "application/json",
      ...requestInit.headers,
    };
  }

  const res = await fetch(url, requestInit);

  if (!res.ok) {
    // 429 Rate Limit : propager le header Retry-After pour que apiFetch puisse attendre
    if (res.status === 429) {
      const retryAfter = res.headers.get('Retry-After');
      throw createApiError('HTTP 429 Too Many Requests', {
        retryAfter: retryAfter ? parseInt(retryAfter, 10) : null,
        status: 429,
      });
    }

    const text = await res.text().catch(() => "");
    throw createApiError(`HTTP ${res.status} ${res.statusText}: ${text}`, {
      responseText: text,
      status: res.status,
    });
  }

  const ct = res.headers.get("content-type") || "";
  if (res.status === 204) return null;
  if (ct.includes("application/json")) return res.json();
  const text = await res.text().catch(() => "");

  // Réponse inattendue (souvent proxy manquant qui renvoie le HTML du site)
  throw createApiError(
    `Réponse API inattendue (non-JSON) pour ${path} – content-type: ${ct || "inconnu"}. ` +
      (text ? `Aperçu: ${text.slice(0, 160)}` : ""),
    {
      contentType: ct,
      responseText: text,
      status: res.status,
    },
  );
};

/**
 * Fonction apiFetch avec système de retry automatique en cas de rebuild serveur
 *
 * Fonctionnalités :
 * - Retry automatique sur les erreurs 502, 503, 504 et network errors
 * - Notification UI via événements navigateur (server:down / server:up / server:failed)
 * - Exponential backoff pour espacer les tentatives
 * - La session salarié voyage dans le cookie `ailix_session` (credentials: "include")
 */
export const apiFetch = async (
  path: string,
  init?: RequestInit,
  retryCount = 0
): Promise<unknown> => {
  // Les endpoints de polling externe (Coolify) sont neutres vis-à-vis du bandeau :
  // ils ne le déclenchent pas et ne le referment pas (un succès monitoring ne prouve
  // pas que le backend est de retour après un vrai rebuild).
  const suppressServerBanner = isBackgroundPollingEndpoint(path);

  try {
    const result = await performFetch(path, init);

    // Serveur de retour après un downtime
    if (isServerCurrentlyDown && !suppressServerBanner) {
      isServerCurrentlyDown = false;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('server:up'));
      }
    }

    return result;

  } catch (error: unknown) {
    const apiError = error as ApiError;

    // 429 Rate Limit : respecter Retry-After et retenter une seule fois
    if (apiError.status === 429 && retryCount === 0) {
      const waitSeconds = Math.min(apiError.retryAfter || 10, 60); // cap 60s
      await sleep(waitSeconds * 1000);
      return apiFetch(path, init, 1);
    }

    const isRetryableError = isNetworkError(apiError);
    const isWriteOperation = isMutation(init);
    const isAuthEndpoint = path.includes('/v1/auth/client/');

    const canRetry = isRetryableError &&
                     (!isWriteOperation || isAuthEndpoint) &&
                     retryCount < MAX_RETRIES &&
                     !suppressServerBanner;

    if (canRetry) {
      // Notifier le banner via événement (pas de toast)
      if (!isServerCurrentlyDown) {
        isServerCurrentlyDown = true;
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('server:down', {
          detail: { retryCount: retryCount + 1, maxRetries: MAX_RETRIES }
        }));
      }

      const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(1.5, retryCount), MAX_RETRY_DELAY);
      await sleep(delay);

      return apiFetch(path, init, retryCount + 1);
    }

    // Erreur finale après épuisement des retries
    if (retryCount >= MAX_RETRIES && isNetworkError(apiError) && !suppressServerBanner) {
      // Notifier le banner qu'on a abandonné
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('server:failed'));
      }
      // Garder l'état "down" actif pour que le prochain succès déclenche bien server:up
    }

    throw error;
  }
};

/**
 * Get headers for admin API calls
 * Returns headers including user email and admin token from storage
 */
export const getAdminHeaders = (): Record<string, string> => {
  const {
    "x-client-id": _ignoredClientId,
    "x-client-user-email": _ignoredClientEmail,
    ...authHeaders
  } = getCurrentBrowserAuthHeaders();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...authHeaders,
  };

  return headers;
};

/**
 * Fonction apiFetch specialisee pour les appels admin
 * Ajoute automatiquement le header x-admin-token
 */
export const adminApiFetch = async (
  path: string,
  init?: RequestInit
): Promise<unknown> => {
  const adminHeaders = getAdminHeaders();

  const mergedInit: RequestInit = {
    ...init,
    headers: {
      ...adminHeaders,
      ...init?.headers,
    },
  };

  return apiFetch(path, mergedInit);
};
